/**
 * NOM035DashboardV2.js
 * Rediseño del Dashboard NOM-035 según M8-K
 * Convierte el dashboard de lista de descargas a centro de análisis visual
 */
import React, { useState, useEffect } from 'react';
import MagicLinkAdmin from './MagicLinkAdmin';
import EmailActionsPanel from './EmailActionsPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Calendar,
  ChevronRight,
  Lock,
  ChevronDown,
  Building2,
  User,
  BarChart3,
  AlertCircle,
  Check,
  X,
  Download,
  Eye,
  Target,
  TrendingUp,
  PieChart,
  Mail,
  Inbox,
  Send,
  Activity,
  FileText,
  FolderOpen,
  PartyPopper,
  Zap,
  RefreshCw,
  History,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import logger from '../utils/logger';

// ============== CONSTANTS ==============

const IPA_COLORS = {
  VERDE: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500', progress: 'bg-green-500', hex: '#27AE60' },
  AMARILLO: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-500', progress: 'bg-yellow-500', hex: '#F39C12' },
  NARANJA: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-500', progress: 'bg-orange-500', hex: '#E67E22' },
  ROJO: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500', progress: 'bg-red-500', hex: '#C0392B' }
};

const RISK_COLORS = {
  NULO: { bg: 'bg-green-100', text: 'text-green-800', badge: 'bg-green-500', hex: '#27AE60', emoji: '🟢' },
  BAJO: { bg: 'bg-blue-100', text: 'text-blue-800', badge: 'bg-blue-500', hex: '#3498DB', emoji: '🟢' },
  MEDIO: { bg: 'bg-yellow-100', text: 'text-yellow-800', badge: 'bg-yellow-500', hex: '#F39C12', emoji: '🟡' },
  ALTO: { bg: 'bg-orange-100', text: 'text-orange-800', badge: 'bg-orange-500', hex: '#E67E22', emoji: '🟠' },
  MUY_ALTO: { bg: 'bg-red-100', text: 'text-red-800', badge: 'bg-red-500', hex: '#C0392B', emoji: '🔴' }
};

const STEP_ORDER = ['DATOS_EMPRESA', 'RESPONSABLE', 'POLITICA', 'DIFUSION', 'GUIA_I', 'GUIA_II_III', 'PROGRAMA', 'EVIDENCIAS', 'COMUNICACION'];

const getIPAStatus = (score) => {
  if (score >= 76) return 'VERDE';
  if (score >= 51) return 'AMARILLO';
  if (score >= 26) return 'NARANJA';
  return 'ROJO';
};

const TEXTS = {
  es: {
    title: 'Cumplimiento NOM-035',
    subtitle: 'Norma Oficial Mexicana para Factores de Riesgo Psicosocial',
    blocks: {
      ipa: 'Índice de Preparación para Auditoría',
      riskDiagnosis: 'Diagnóstico de Riesgo Psicosocial',
      participation: 'Participación en Evaluación',
      ats: 'Acontec. Traumáticos Severos',
      program: 'Programa de Atención NOM-035',
      expediente: 'Expediente de Cumplimiento',
      analytics: 'Análisis y Datos'
    },
    ipaMessages: {
      VERDE: 'Tu empresa está lista para una inspección sorpresa de la STPS',
      AMARILLO: 'Buen avance. Completa las acciones pendientes para estar protegido',
      NARANJA: 'Riesgo de multa. Acelera el cumplimiento',
      ROJO: 'Riesgo alto de multa. Actúa de inmediato'
    },
    stepsCompleted: 'pasos completados',
    daysUntilReeval: 'días para reevaluación',
    downloadReports: 'Descargar reportes',
    viewDetails: 'Ver detalle',
    completed: 'Completados',
    minimum: 'Mínimo',
    legalMinimum: 'Mínimo legal alcanzado',
    pending: 'pendientes para el mínimo',
    men: 'Hombres',
    women: 'Mujeres',
    evaluated: 'Evaluados',
    withExposure: 'Con exposición',
    referred: 'Canalizados',
    noPendingCases: 'Sin casos pendientes',
    urgentAts: 'URGENTE: trabajador(es) con ATS identificado(s) sin canalizar',
    businessDays: 'días hábiles transcurridos. Plazo legal: 5 días hábiles.',
    goToReferral: 'Ir a canalización',
    evidenced: 'Evidenciadas',
    inProgress: 'En progreso',
    overdue: 'Vencidas',
    overdueAction: 'acción vencida sin evidencia',
    viewFullProgram: 'Ver programa completo',
    uploadEvidence: 'Cargar evidencia',
    ipaTrend: 'Tendencia IPA',
    riskByArea: 'Riesgo por Área',
    mailbox30d: 'Buzón — Últimos 30d',
    received: 'Recibidos',
    attended: 'Atendidos',
    mostFrequent: 'Categoría más frecuente',
    viewHistory: 'Ver historial',
    viewByArea: 'Ver por área',
    viewMailbox: 'Ver buzón',
    domainDistribution: 'Distribución Global',
    top3Critical: 'TOP 3 DOMINIOS CRÍTICOS',
    employees: 'emp.',
    viewAreaAnalysis: 'Ver análisis por área',
    applyGuide: 'Aplicar Guía',
    noGuideApplied: 'Aplica la Guía III para ver el diagnóstico de riesgo psicosocial',
    fileReady: 'Expediente listo para auditoría',
    incomplete: 'secciones incompletas'
  },
  en: {
    title: 'NOM-035 Compliance',
    subtitle: 'Mexican Official Standard for Psychosocial Risk Factors',
    blocks: {
      ipa: 'Audit Preparation Index',
      riskDiagnosis: 'Psychosocial Risk Diagnosis',
      participation: 'Evaluation Participation',
      ats: 'Severe Traumatic Events',
      program: 'NOM-035 Attention Program',
      expediente: 'Compliance File',
      analytics: 'Analysis and Data'
    },
    ipaMessages: {
      VERDE: 'Your company is ready for a surprise STPS inspection',
      AMARILLO: 'Good progress. Complete pending actions to be protected',
      NARANJA: 'Penalty risk. Speed up compliance',
      ROJO: 'High penalty risk. Act immediately'
    },
    stepsCompleted: 'steps completed',
    daysUntilReeval: 'days until reevaluation',
    downloadReports: 'Download reports',
    viewDetails: 'View details',
    completed: 'Completed',
    minimum: 'Minimum',
    legalMinimum: 'Legal minimum reached',
    pending: 'pending for minimum',
    men: 'Men',
    women: 'Women',
    evaluated: 'Evaluated',
    withExposure: 'With exposure',
    referred: 'Referred',
    noPendingCases: 'No pending cases',
    urgentAts: 'URGENT: worker(s) with identified STE not referred',
    businessDays: 'business days elapsed. Legal deadline: 5 business days.',
    goToReferral: 'Go to referral',
    evidenced: 'Evidenced',
    inProgress: 'In progress',
    overdue: 'Overdue',
    overdueAction: 'overdue action without evidence',
    viewFullProgram: 'View full program',
    uploadEvidence: 'Upload evidence',
    ipaTrend: 'IPA Trend',
    riskByArea: 'Risk by Area',
    mailbox30d: 'Mailbox — Last 30d',
    received: 'Received',
    attended: 'Attended',
    mostFrequent: 'Most frequent category',
    viewHistory: 'View history',
    viewByArea: 'View by area',
    viewMailbox: 'View mailbox',
    domainDistribution: 'Global Distribution',
    top3Critical: 'TOP 3 CRITICAL DOMAINS',
    employees: 'emp.',
    viewAreaAnalysis: 'View area analysis',
    applyGuide: 'Apply Guide',
    noGuideApplied: 'Apply Guide III to see the psychosocial risk diagnosis',
    fileReady: 'File ready for audit',
    incomplete: 'incomplete sections'
  }
};

// Expediente sections - exactly 9 steps matching the configuration wizard
const EXPEDIENTE_SECTIONS = [
  { step: 'DATOS_EMPRESA', num: 1, name_es: '1. Datos del centro de trabajo', name_en: '1. Workplace data' },
  { step: 'RESPONSABLE', num: 2, name_es: '2. Responsable NOM-035', name_en: '2. NOM-035 Responsible' },
  { step: 'POLITICA', num: 3, name_es: '3. Política de prevención', name_en: '3. Prevention policy' },
  { step: 'DIFUSION', num: 4, name_es: '4. Difusión de la política', name_en: '4. Policy diffusion' },
  { step: 'GUIA_I', num: 5, name_es: '5. Guía I (ATS)', name_en: '5. Guide I (STE)' },
  { step: 'GUIA_II_III', num: 6, name_es: '6. Guía II/III', name_en: '6. Guide II/III' },
  { step: 'PROGRAMA', num: 7, name_es: '7. Programa de atención', name_en: '7. Attention program' },
  { step: 'EVIDENCIAS', num: 8, name_es: '8. Evidencias', name_en: '8. Evidence' },
  { step: 'COMUNICACION', num: 9, name_es: '9. Comunicación', name_en: '9. Communication' }
];

// ============== DASHBOARD COMPONENT ==============

const NOM035DashboardV2 = ({
  empresaId,
  onOpenConfig,
  onOpenGuide1,
  onOpenGuide3,
  onOpenPrograma,
  onOpenExpediente,
  onOpenReports,
  onOpenBuzonAdmin,
  onOpenBuzonEmpleado,
  onOpenATSReferral
}) => {
  const { language } = useLanguage();
  const texts = TEXTS[language] || TEXTS.es;
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [participation, setParticipation] = useState(null);
  const [atsStats, setAtsStats] = useState(null);
  const [programStats, setProgramStats] = useState({ total: 0, evidenciadas: 0, enProgreso: 0, vencidas: 0, accionesVencidas: [] });
  const [buzonStats, setBuzonStats] = useState({ total_recibidos: 0, pendientes: 0, atendidos: 0 });
  const [availableReports, setAvailableReports] = useState({});
  const [ipaHistory, setIpaHistory] = useState([]);
  const [areaRisk, setAreaRisk] = useState([]);
  const [showReeval, setShowReeval] = useState(false);
  const [reevalMotivo, setReevalMotivo] = useState('');
  const [reevalDetalle, setReevalDetalle] = useState('');
  const [reevalLoading, setReevalLoading] = useState(false);
  const [cycleHistory, setCycleHistory] = useState([]);

  // M-06: Hierarchical drill-down state
  const [hierData, setHierData] = useState(null);
  const [drillLevel, setDrillLevel] = useState('client'); // client | razon_social | center
  const [selectedRS, setSelectedRS] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);

  const REEVAL_MOTIVOS = [
    { value: 'cambio_actividad', label_es: 'Cambio de actividad/proceso', label_en: 'Activity/process change' },
    { value: 'cambio_organizacional', label_es: 'Cambio organizacional mayor', label_en: 'Major organizational change' },
    { value: 'accidente_grave', label_es: 'Accidente de trabajo grave', label_en: 'Serious workplace accident' },
    { value: 'denuncia_formal', label_es: 'Denuncia formal recibida', label_en: 'Formal complaint received' },
    { value: 'otro', label_es: 'Otro motivo', label_en: 'Other reason' }
  ];

  const handleReevaluacion = async () => {
    if (!reevalMotivo) {
      toast.error(language === 'es' ? 'Selecciona un motivo' : 'Select a reason');
      return;
    }
    setReevalLoading(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/reevaluacion-anticipada`, {
        motivo: reevalMotivo,
        detalle: reevalDetalle
      });
      if (res.data.success) {
        toast.success(language === 'es' ? 'Reevaluación anticipada creada' : 'Early reevaluation created');
        setShowReeval(false);
        setReevalMotivo('');
        setReevalDetalle('');
        loadAllData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setReevalLoading(false);
    }
  };

  const handleRegenerateProgram = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/programa-atencion/generate`);
      if (res.data.success) {
        toast.success(language === 'es' ? 'Tareas generadas correctamente' : 'Tasks generated successfully');
        loadAllData(); // Reload all data
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al generar tareas' : 'Error generating tasks');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (empresaId) {
      loadAllData();
    }
  }, [empresaId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [dashRes, configRes, participationRes, programRes, buzonRes, reportsRes] = await Promise.all([
        axios.get(`/v5/nom035/${empresaId}/dashboard`),
        axios.get(`/v5/nom035/${empresaId}/config`),
        axios.get(`/v5/nom035/${empresaId}/guide3/participation`).catch(() => ({ data: { success: false } })),
        axios.get(`/v5/nom035/${empresaId}/programa-atencion`).catch(() => ({ data: { success: false } })),
        axios.get(`/v5/nom035/${empresaId}/buzon/stats`).catch(() => ({ data: { success: false } })),
        axios.get(`/v5/nom035-reports/available-reports/${empresaId}`).catch(() => ({ data: { success: false } }))
      ]);

      if (dashRes.data.success) {
        setDashboard(dashRes.data.data);
        
        // Extract ATS stats from dashboard
        const alertas = dashRes.data.data.alertas || [];
        const atsAlert = alertas.find(a => a.tipo === 'ATS_PENDIENTE');
        setAtsStats({
          evaluated: dashRes.data.data.numero_trabajadores || 0,
          withExposure: atsAlert ? parseInt(atsAlert.message_es?.match(/\d+/)?.[0] || '0') : 0,
          referred: 0,
          pendingCount: atsAlert ? parseInt(atsAlert.message_es?.match(/\d+/)?.[0] || '0') : 0,
          daysSinceIdentification: 3 // This would come from backend
        });
      }

      if (configRes.data?.success) {
        setConfig(configRes.data.data);
      }

      if (participationRes.data?.success) {
        setParticipation(participationRes.data.data);
      }

      if (programRes.data?.success) {
        const programa = programRes.data.data;
        // Use stats from backend directly (already calculated)
        const stats = programa?.stats || {};
        
        // Calculate vencidas actions for display
        const planes = programa?.planes || [];
        const accionesVencidas = [];
        planes.forEach(plan => {
          (plan.tareas || []).forEach(tarea => {
            if (tarea.estado !== 'EVIDENCIADA' && tarea.estado !== 'COMPLETADA' && 
                tarea.fecha_limite && new Date(tarea.fecha_limite) < new Date()) {
              accionesVencidas.push({
                id: tarea.id,
                nombre: tarea.titulo,
                diasVencida: Math.floor((new Date() - new Date(tarea.fecha_limite)) / (1000 * 60 * 60 * 24))
              });
            }
          });
        });
        
        setProgramStats({
          total: stats.total || 0,
          evidenciadas: stats.evidenciadas || 0,
          pendientes: (stats.pendientes || 0) + (stats.en_progreso || 0),
          enProgreso: stats.en_progreso || 0,
          vencidas: stats.vencidas || 0,
          accionesVencidas: accionesVencidas.slice(0, 2),
          planesCount: planes.length // Track number of plans
        });
      }

      if (buzonRes.data?.success) {
        setBuzonStats(buzonRes.data.data || { total_recibidos: 0, pendientes: 0, atendidos: 0 });
      }

      if (reportsRes.data?.success) {
        setAvailableReports(reportsRes.data.data || {});
      }

      // Mock IPA history (would come from backend)
      setIpaHistory([
        { month: 'Oct', value: 78 },
        { month: 'Nov', value: 85 },
        { month: 'Dic', value: 92 },
        { month: 'Ene', value: dashboard?.ipa?.score || 100 }
      ]);

      // Mock area risk (would come from backend heatmap by area)
      setAreaRisk([
        { name: 'Producción', level: 'ALTO' },
        { name: 'Ventas', level: 'MEDIO' },
        { name: 'Admin', level: 'BAJO' },
        { name: 'RRHH', level: 'BAJO' }
      ]);

      // Load cycle history
      try {
        const cyclesRes = await axios.get(`/v5/nom035/${empresaId}/ciclos`);
        if (cyclesRes.data?.success) {
          setCycleHistory(cyclesRes.data.data || []);
        }
      } catch (e) { /* ignore */ }

      // M-06: Load hierarchical dashboard
      try {
        const hierRes = await axios.get(`/v5/nom035/${empresaId}/dashboard-hierarchical`);
        if (hierRes.data?.success) {
          setHierData(hierRes.data.data);
        }
      } catch (e) { logger.error('Error loading hierarchical data:', e); }

    } catch (error) {
      logger.error('Error loading dashboard data:', error);
      toast.error(language === 'es' ? 'Error al cargar datos' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!dashboard?.configured) {
    return (
      <Card className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold mb-2">
          {language === 'es' ? 'Módulo NOM-035 no configurado' : 'NOM-035 module not configured'}
        </h3>
        <p className="text-gray-500 mb-4">
          {language === 'es' 
            ? 'Configura el módulo para comenzar a cumplir con la norma'
            : 'Configure the module to start complying with the standard'}
        </p>
        <Button onClick={onOpenConfig}>
          {language === 'es' ? 'Configurar ahora' : 'Configure now'}
        </Button>
      </Card>
    );
  }

  const ipaScore = dashboard?.ipa?.score || 0;
  const ipaStatus = getIPAStatus(ipaScore);
  const ipaColors = IPA_COLORS[ipaStatus];
  
  // Calculate completed steps from actual config data
  const completedSteps = config?.pasos_completados 
    ? STEP_ORDER.filter(step => config.pasos_completados[step] === true).length 
    : 0;
  
  const guideApplied = dashboard?.heatmap_dominios?.some(d => d.nivel_predominante);
  const hasAreas = areaRisk.length > 0;
  const hasIpaHistory = ipaHistory.length >= 2;

  const calculateDaysUntil = (dateStr) => {
    if (!dateStr) return 0;
    const diff = new Date(dateStr) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const isStepCompleted = (stepKey) => {
    return config?.pasos_completados?.[stepKey] === true;
  };

  // Calculate global risk distribution
  const calculateGlobalDistribution = () => {
    if (!dashboard?.heatmap_dominios?.length) return null;
    
    let totalNulo = 0, totalBajo = 0, totalMedio = 0, totalAlto = 0, totalMuyAlto = 0;
    let count = 0;
    
    dashboard.heatmap_dominios.forEach(d => {
      if (d.distribucion) {
        totalNulo += d.distribucion.nulo || 0;
        totalBajo += d.distribucion.bajo || 0;
        totalMedio += d.distribucion.medio || 0;
        totalAlto += d.distribucion.alto || 0;
        totalMuyAlto += d.distribucion.muy_alto || 0;
        count++;
      }
    });

    if (count === 0) return null;
    
    const total = totalNulo + totalBajo + totalMedio + totalAlto + totalMuyAlto;
    if (total === 0) return null;

    return {
      nuloBajo: Math.round((totalNulo + totalBajo) / count),
      medio: Math.round(totalMedio / count),
      alto: Math.round(totalAlto / count),
      muyAlto: Math.round(totalMuyAlto / count)
    };
  };

  // Get top 3 critical domains
  const getTop3CriticalDomains = () => {
    if (!dashboard?.heatmap_dominios?.length) return [];
    
    const riskOrder = { 'MUY_ALTO': 4, 'ALTO': 3, 'MEDIO': 2, 'BAJO': 1, 'NULO': 0 };
    
    return [...dashboard.heatmap_dominios]
      .filter(d => d.nivel_predominante)
      .sort((a, b) => (riskOrder[b.nivel_predominante] || 0) - (riskOrder[a.nivel_predominante] || 0))
      .slice(0, 3)
      .map(d => ({
        nombre: d.nombre,
        nivel: d.nivel_predominante,
        empleados: d.total_respondentes || 0
      }));
  };

  const globalDist = calculateGlobalDistribution();
  const top3Domains = getTop3CriticalDomains();

  // Calculate participation stats - show 0 if no surveys have been completed
  // Only use real data from the participation endpoint, never fake defaults
  const hasRealParticipationData = participation && typeof participation.completados === 'number';
  const participationStats = hasRealParticipationData ? participation : {
    completados: 0,
    minimo_requerido: Math.ceil((dashboard?.numero_trabajadores || 0) * 0.9) || 1,
    porcentaje: 0,
    por_genero: { 
      hombres: { completados: 0, minimo: 0 }, 
      mujeres: { completados: 0, minimo: 0 } 
    }
  };
  const hasMinimumParticipation = participationStats.completados > 0 && participationStats.completados >= participationStats.minimo_requerido;

  // Program progress percentage
  const programProgress = programStats.total > 0 
    ? Math.round((programStats.evidenciadas / programStats.total) * 100) 
    : 0;
  const programProgressColor = programProgress >= 80 ? 'bg-green-500' : programProgress >= 50 ? 'bg-orange-500' : 'bg-red-500';

  // Expediente status - use real config data
  const expedienteComplete = EXPEDIENTE_SECTIONS.filter(s => isStepCompleted(s.step)).length;
  const expedienteTotal = EXPEDIENTE_SECTIONS.length;

  // ATS urgency
  const hasUrgentATS = atsStats?.pendingCount > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{texts.title}</h2>
          <p className="text-sm text-gray-500">{texts.subtitle}</p>
        </div>
        <Button variant="outline" onClick={onOpenExpediente} data-testid="export-expediente-btn">
          <Download className="w-4 h-4 mr-2" />
          {language === 'es' ? 'Exportar expediente' : 'Export file'}
        </Button>
      </div>

      {/* M-06: Hierarchical Drill-down Navigation */}
      {hierData && hierData.razones_sociales?.length > 0 && (
        <Card data-testid="m06-drilldown">
          <CardContent className="p-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-3" data-testid="m06-breadcrumb">
              <button onClick={() => { setDrillLevel('client'); setSelectedRS(null); setSelectedCenter(null); }}
                className={`font-medium ${drillLevel === 'client' ? 'text-teal-700' : 'text-gray-500 hover:text-teal-600'}`}>
                {language === 'es' ? 'Empresa' : 'Company'}
              </button>
              {selectedRS && (
                <>
                  <ChevronRight className="h-3 w-3 text-gray-400" />
                  <button onClick={() => { setDrillLevel('razon_social'); setSelectedCenter(null); }}
                    className={`font-medium ${drillLevel === 'razon_social' ? 'text-teal-700' : 'text-gray-500 hover:text-teal-600'}`}>
                    {selectedRS.nombre}
                  </button>
                </>
              )}
              {selectedCenter && (
                <>
                  <ChevronRight className="h-3 w-3 text-gray-400" />
                  <span className="font-medium text-teal-700">{selectedCenter.nombre}</span>
                </>
              )}
            </div>

            {/* Level Content */}
            {drillLevel === 'client' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500">IPA {language === 'es' ? 'Ponderado' : 'Weighted'}</span>
                    <p className="text-2xl font-bold" style={{ color: hierData.client.ipa_status === 'VERDE' ? '#059669' : hierData.client.ipa_status === 'AMARILLO' ? '#d97706' : '#dc2626' }}>
                      {hierData.client.weighted_ipa}%
                    </p>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>{hierData.client.centers_count} {language === 'es' ? 'centros' : 'centers'}</p>
                    <p>{hierData.client.total_completadas}/{hierData.client.total_trabajadores} {language === 'es' ? 'evaluados' : 'evaluated'}</p>
                  </div>
                </div>
                {/* RS cards */}
                <div className="grid gap-2">
                  {hierData.razones_sociales.map(rs => (
                    <button key={rs.id} onClick={() => { setSelectedRS(rs); setDrillLevel('razon_social'); }}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 text-left transition-colors"
                      data-testid={`m06-rs-${rs.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${rs.ipa_status === 'VERDE' ? 'bg-green-500' : rs.ipa_status === 'AMARILLO' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                        <div>
                          <p className="font-medium text-sm">{rs.nombre}</p>
                          <p className="text-xs text-gray-500">{rs.centers_count} {language === 'es' ? 'centros' : 'centers'} · {rs.total_trabajadores} {language === 'es' ? 'trabajadores' : 'workers'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{rs.weighted_ipa}%</span>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {drillLevel === 'razon_social' && selectedRS && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500">IPA {language === 'es' ? 'Ponderado' : 'Weighted'} — {selectedRS.nombre}</span>
                    <p className="text-2xl font-bold" style={{ color: selectedRS.ipa_status === 'VERDE' ? '#059669' : selectedRS.ipa_status === 'AMARILLO' ? '#d97706' : '#dc2626' }}>
                      {selectedRS.weighted_ipa}%
                    </p>
                  </div>
                  {selectedRS.ats_pending > 0 && (
                    <Badge className="bg-red-100 text-red-700">ATS: {selectedRS.ats_pending}</Badge>
                  )}
                </div>
                <div className="grid gap-2">
                  {selectedRS.centers.map(c => (
                    <button key={c.id} onClick={() => { setSelectedCenter(c); setDrillLevel('center'); }}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 text-left transition-colors"
                      data-testid={`m06-center-${c.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${c.ipa_status === 'VERDE' ? 'bg-green-500' : c.ipa_status === 'AMARILLO' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                        <div>
                          <p className="font-medium text-sm">{c.nombre}</p>
                          <p className="text-xs text-gray-500">{c.completadas}/{c.num_trabajadores} {language === 'es' ? 'evaluados' : 'evaluated'} · {language === 'es' ? 'Guía' : 'Guide'} {c.guia || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!c.meets_threshold && <Lock className="h-3.5 w-3.5 text-gray-400" />}
                        <span className="font-semibold text-sm">{c.ipa_score}%</span>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {drillLevel === 'center' && selectedCenter && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500">IPA — {selectedCenter.nombre}</span>
                    <p className="text-2xl font-bold" style={{ color: selectedCenter.ipa_status === 'VERDE' ? '#059669' : selectedCenter.ipa_status === 'AMARILLO' ? '#d97706' : '#dc2626' }}>
                      {selectedCenter.ipa_score}%
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <p>{selectedCenter.completadas}/{selectedCenter.num_trabajadores} {language === 'es' ? 'evaluados' : 'evaluated'}</p>
                    <p>{language === 'es' ? 'Guía' : 'Guide'} {selectedCenter.guia || '-'} · {language === 'es' ? 'Piso' : 'Floor'}: {selectedCenter.participation?.piso}</p>
                  </div>
                </div>

                {/* Threshold message */}
                {!selectedCenter.meets_threshold && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center gap-2" data-testid="m06-threshold-msg">
                    <Lock className="h-4 w-4 shrink-0" />
                    {selectedCenter.threshold_message}
                  </div>
                )}

                {/* Domain risk heatmap (only if meets threshold) */}
                {selectedCenter.meets_threshold && selectedCenter.domain_risk && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">{language === 'es' ? 'Riesgo por Dominio' : 'Risk by Domain'}</p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {Object.entries(selectedCenter.domain_risk).map(([dk, avg]) => {
                        const level = avg > 20 ? 'high' : avg > 10 ? 'medium' : 'low';
                        const colors = { high: 'bg-red-100 text-red-800 border-red-200', medium: 'bg-yellow-100 text-yellow-800 border-yellow-200', low: 'bg-green-100 text-green-800 border-green-200' };
                        return (
                          <div key={dk} className={`p-2 rounded border text-center ${colors[level]}`}>
                            <p className="text-xs opacity-70">D{dk}</p>
                            <p className="text-sm font-semibold">{avg}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ATS stats (exempt from threshold) */}
                {(selectedCenter.ats_count > 0 || selectedCenter.ats_pending > 0) && (
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">ATS:</span>
                    <Badge className="bg-blue-100 text-blue-700">{selectedCenter.ats_count} {language === 'es' ? 'identificados' : 'identified'}</Badge>
                    {selectedCenter.ats_pending > 0 && (
                      <Badge className="bg-red-100 text-red-700">{selectedCenter.ats_pending} {language === 'es' ? 'pendientes' : 'pending'}</Badge>
                    )}
                  </div>
                )}

                {/* M-09: Email action buttons */}
                <EmailActionsPanel
                  empresaId={empresaId}
                  centroId={selectedCenter.id}
                  centroNombre={selectedCenter.nombre}
                  language={language}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* BANNER ATS URGENTE (Condicional - Máxima prioridad visual) */}
      {hasUrgentATS && (
        <Card className="border-2 border-red-500 bg-red-50 animate-pulse" data-testid="ats-urgent-banner">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-600" />
                <div>
                  <p className="font-bold text-red-800">
                    🚨 {texts.urgentAts.replace('trabajador(es)', atsStats.pendingCount.toString())}
                  </p>
                  <p className="text-sm text-red-700">
                    {language === 'es' ? `Han transcurrido ${atsStats.daysSinceIdentification} ${texts.businessDays}` : `${atsStats.daysSinceIdentification} ${texts.businessDays}`}
                  </p>
                </div>
              </div>
              <Button variant="destructive" onClick={onOpenATSReferral} data-testid="ats-referral-btn">
                → {texts.goToReferral}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BLOQUE 1: IPA - Sin cambios significativos */}
      <Card className={`${ipaColors.bg} border-2 ${ipaColors.border}`} data-testid="ipa-block">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5" style={{ color: ipaColors.hex }} />
            {texts.blocks.ipa}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <p className={`text-sm ${ipaColors.text}`}>{texts.ipaMessages[ipaStatus]}</p>
            </div>
            <div className="text-right">
              <span className={`text-5xl font-bold ${ipaColors.text}`}>{ipaScore}%</span>
              <p className="text-sm text-gray-600">{completedSteps}/9 {texts.stepsCompleted}</p>
            </div>
          </div>
          
          {/* Mini progress bar of 9 steps */}
          <div className="flex items-center gap-1 mb-4">
            {STEP_ORDER.map((stepKey, idx) => (
              <div 
                key={stepKey}
                className={`flex-1 h-2 rounded ${idx < completedSteps ? 'bg-green-500' : 'bg-gray-300'}`}
              />
            ))}
          </div>
          
          {ipaScore >= 100 && (
            <div className="mt-4 p-3 bg-green-200 rounded-lg flex items-center gap-2">
              <PartyPopper className="w-5 h-5 text-green-700" />
              <span className="text-green-800 font-medium text-sm">
                {language === 'es' ? '¡Felicidades! Empresa protegida.' : 'Congratulations! Company protected.'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BLOQUE 2: DIAGNÓSTICO DE RIESGO PSICOSOCIAL (Mapa de Calor) - Rediseñado */}
      <Card data-testid="risk-diagnosis-block">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-teal-600" />
              {texts.blocks.riskDiagnosis}
            </CardTitle>
            {guideApplied && (
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  <Calendar className="w-3 h-3 mr-1" />
                  {dashboard?.fecha_reevaluacion && new Date(dashboard.fecha_reevaluacion).toLocaleDateString()}
                </Badge>
                <Badge variant="outline" className="text-teal-700">
                  {calculateDaysUntil(dashboard?.fecha_reevaluacion)} {texts.daysUntilReeval}
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => onOpenReports?.('R03')}>
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {guideApplied ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Mapa de Calor - 9 Dominios */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">
                  {language === 'es' ? 'MAPA DE CALOR — 9 DOMINIOS' : 'HEAT MAP — 9 DOMAINS'}
                </p>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {dashboard.heatmap_dominios?.map((domain, idx) => {
                    const nivel = domain.nivel_predominante || 'NULO';
                    const riskColor = RISK_COLORS[nivel] || RISK_COLORS.NULO;
                    return (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${riskColor.bg}`}
                        onClick={() => {}}
                        data-testid={`domain-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{domain.nombre}</span>
                          <div className="flex items-center gap-2">
                            <span>{riskColor.emoji}</span>
                            <Badge className={`${riskColor.badge} text-white`}>
                              {language === 'es' 
                                ? nivel.replace('_', ' ')
                                : nivel.replace('MUY_ALTO', 'VERY HIGH').replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">
                  {language === 'es' ? 'Cada dominio es clickable → detalle de empleados afectados' : 'Each domain is clickable → affected employees detail'}
                </p>
              </div>
              
              {/* Right: Distribución Global + Top 3 */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">{texts.domainDistribution}</p>
                
                {globalDist && (
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-20 text-gray-600">{language === 'es' ? 'Nulo/Bajo' : 'Null/Low'}</span>
                      <div className="flex-1 h-4 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full bg-green-500" style={{ width: `${globalDist.nuloBajo}%` }} />
                      </div>
                      <span className="text-xs font-medium w-10 text-right">{globalDist.nuloBajo}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-20 text-gray-600">{language === 'es' ? 'Medio' : 'Medium'}</span>
                      <div className="flex-1 h-4 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full bg-yellow-500" style={{ width: `${globalDist.medio}%` }} />
                      </div>
                      <span className="text-xs font-medium w-10 text-right">{globalDist.medio}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-20 text-gray-600">{language === 'es' ? 'Alto' : 'High'}</span>
                      <div className="flex-1 h-4 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full bg-orange-500" style={{ width: `${globalDist.alto}%` }} />
                      </div>
                      <span className="text-xs font-medium w-10 text-right">{globalDist.alto}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-20 text-gray-600">{language === 'es' ? 'Muy Alto' : 'Very High'}</span>
                      <div className="flex-1 h-4 bg-gray-200 rounded overflow-hidden">
                        <div className="h-full bg-red-500" style={{ width: `${globalDist.muyAlto}%` }} />
                      </div>
                      <span className="text-xs font-medium w-10 text-right">{globalDist.muyAlto}%</span>
                    </div>
                  </div>
                )}

                {top3Domains.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-bold text-gray-700 mb-2">{texts.top3Critical}:</p>
                    <div className="space-y-1">
                      {top3Domains.map((d, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span>{idx + 1}. {d.nombre}</span>
                          <span className="text-gray-500">— {d.empleados} {texts.employees}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button variant="outline" className="w-full mt-4" onClick={() => onOpenReports?.('R07')}>
                  {texts.viewAreaAnalysis} →
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-4">{texts.noGuideApplied}</p>
              <Button onClick={onOpenGuide3}>
                <Activity className="w-4 h-4 mr-2" />
                {texts.applyGuide} III
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BLOQUE 3: PARTICIPACIÓN Y ATS - NUEVO */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel Participación */}
        <Card data-testid="participation-block">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-blue-600" />
              {texts.blocks.participation}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-2xl font-bold">{participationStats.completados || 0} / {participationStats.minimo_requerido || 1}</span>
                <span className="text-sm text-gray-500">{texts.completed}</span>
              </div>
              <Progress value={Math.min(participationStats.minimo_requerido > 0 ? (participationStats.completados / participationStats.minimo_requerido) * 100 : 100, 100)} className="h-3" />
              <div className="flex items-center justify-between mt-2">
                <Badge className={hasMinimumParticipation ? 'bg-green-500' : 'bg-orange-500'}>
                  {hasMinimumParticipation 
                    ? `✅ ${texts.legalMinimum}` 
                    : `⏳ ${Math.max(0, (participationStats.minimo_requerido || 0) - (participationStats.completados || 0))} ${texts.pending}`}
                </Badge>
                <span className="text-sm font-medium">
                  {participationStats.minimo_requerido > 0 
                    ? Math.round((participationStats.completados / participationStats.minimo_requerido) * 100) 
                    : 100}%
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{texts.men}</span>
                <span className="text-sm font-medium">
                  {participationStats.por_genero?.hombres?.completados || 0}/{participationStats.por_genero?.hombres?.minimo || 0}
                  {(participationStats.por_genero?.hombres?.completados || 0) >= (participationStats.por_genero?.hombres?.minimo || 0) ? ' ✅' : ' ⏳'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{texts.women}</span>
                <span className="text-sm font-medium">
                  {participationStats.por_genero?.mujeres?.completados || 0}/{participationStats.por_genero?.mujeres?.minimo || 0}
                  {(participationStats.por_genero?.mujeres?.completados || 0) >= (participationStats.por_genero?.mujeres?.minimo || 0) ? ' ✅' : ' ⏳'}
                </span>
              </div>
            </div>

            <Button variant="outline" className="w-full mt-4" onClick={() => onOpenReports?.('R08')}>
              {language === 'es' ? 'Ver participación' : 'View participation'} →
            </Button>
          </CardContent>
        </Card>

        {/* Panel ATS */}
        <Card className={hasUrgentATS ? 'border-red-300' : ''} data-testid="ats-block">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className={`w-5 h-5 ${hasUrgentATS ? 'text-red-600' : 'text-gray-600'}`} />
              {texts.blocks.ats}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!hasUrgentATS ? (
              <div className="text-center py-4">
                <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                <p className="text-green-700 font-medium">✅ {texts.noPendingCases}</p>
              </div>
            ) : (
              <div className="text-center py-2">
                <AlertTriangle className="w-10 h-10 mx-auto text-red-500 mb-2" />
                <p className="text-red-700 font-medium">{atsStats.pendingCount} {language === 'es' ? 'casos pendientes' : 'pending cases'}</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="text-center p-2 bg-gray-50 rounded">
                <span className="text-xl font-bold">{atsStats?.evaluated || dashboard?.numero_trabajadores || 0}</span>
                <p className="text-xs text-gray-500">{texts.evaluated}</p>
              </div>
              <div className="text-center p-2 bg-orange-50 rounded">
                <span className="text-xl font-bold text-orange-600">{atsStats?.withExposure || 0}</span>
                <p className="text-xs text-gray-500">{texts.withExposure}</p>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <span className="text-xl font-bold text-green-600">{atsStats?.referred || 0}</span>
                <p className="text-xs text-gray-500">{texts.referred}</p>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full mt-4" 
              onClick={() => onOpenReports?.('R02')}
              data-testid="view-ats-detail-btn"
            >
              {language === 'es' ? 'Ver detalle ATS' : 'View STE detail'} →
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* BLOQUE 4: PROGRAMA DE ATENCIÓN - Enriquecido */}
      <Card data-testid="program-block">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5 text-teal-600" />
            {texts.blocks.program}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Barra de progreso */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">{language === 'es' ? 'Avance general:' : 'Overall progress:'} {programProgress}%</span>
              <span className="text-sm text-gray-500">{programStats.evidenciadas} de {programStats.total} {language === 'es' ? 'acciones evidenciadas' : 'actions evidenced'}</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full ${programProgressColor} transition-all`} style={{ width: `${programProgress}%` }} />
            </div>
          </div>

          {/* Contadores */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <span className="text-2xl font-bold text-green-700">{programStats.evidenciadas}</span>
              <p className="text-xs text-gray-500">✅ {texts.evidenced}</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <span className="text-2xl font-bold text-yellow-700">{programStats.pendientes}</span>
              <p className="text-xs text-gray-500">⏳ {language === 'es' ? 'Pendientes' : 'Pending'}</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <span className="text-2xl font-bold text-red-700">{programStats.vencidas}</span>
              <p className="text-xs text-gray-500">🔴 {texts.overdue}</p>
            </div>
          </div>

          {/* Acciones vencidas urgentes */}
          {programStats.accionesVencidas.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-red-800 mb-2">
                ⚠ {programStats.vencidas} {texts.overdueAction}:
              </p>
              {programStats.accionesVencidas.map((accion, idx) => (
                <div key={idx} className="flex items-center justify-between py-1">
                  <span className="text-sm text-red-700">"{accion.nombre}" — {language === 'es' ? `venció hace ${accion.diasVencida} días` : `overdue ${accion.diasVencida} days`}</span>
                  <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={() => onOpenPrograma?.()}>
                    {texts.uploadEvidence}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Botón para regenerar tareas si no hay ninguna pero hay planes */}
          {(programStats.total === 0 || (programStats.planesCount > 0 && programStats.total === 0)) && (
            <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg mb-4 text-center">
              <RefreshCw className="w-6 h-6 mx-auto text-yellow-600 mb-2" />
              <p className="text-sm text-yellow-700 mb-3">
                {language === 'es' 
                  ? 'No hay tareas en el programa. Genera las tareas basadas en los resultados de Guía III.'
                  : 'No tasks in program. Generate tasks based on Guide III results.'}
              </p>
              <Button 
                onClick={handleRegenerateProgram} 
                disabled={generating}
                variant="outline" 
                className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
              >
                {generating && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                <Zap className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Generar Tareas' : 'Generate Tasks'}
              </Button>
            </div>
          )}

          <Button variant="outline" className="w-full" onClick={onOpenPrograma}>
            {texts.viewFullProgram} →
          </Button>
        </CardContent>
      </Card>

      {/* GRID: Expediente + Análisis y Datos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* BLOQUE 5: EXPEDIENTE DE CUMPLIMIENTO - Sin cambios significativos */}
        <Card data-testid="expediente-block">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderOpen className="w-5 h-5 text-teal-600" />
              {texts.blocks.expediente}
            </CardTitle>
            <CardDescription>
              {expedienteComplete === expedienteTotal ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {texts.fileReady}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-orange-600">
                  <AlertCircle className="w-4 h-4" />
                  {expedienteTotal - expedienteComplete} {texts.incomplete}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {EXPEDIENTE_SECTIONS.map((section, idx) => {
                const isComplete = isStepCompleted(section.step);
                return (
                  <div 
                    key={idx}
                    className={`p-2 rounded border ${
                      isComplete 
                        ? 'bg-green-50 border-green-200 text-green-800' 
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isComplete ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      <span className="text-xs">{language === 'es' ? section.name_es : section.name_en}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* BLOQUE 6: ANÁLISIS Y DATOS (ex-Reportes) - Rediseñado */}
        <Card data-testid="analytics-block">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                {texts.blocks.analytics}
              </CardTitle>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-1" />
                    {texts.downloadReports}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => onOpenReports?.('R01')} disabled={!availableReports.R01?.available}>
                    <FileText className="w-4 h-4 mr-2" />
                    R01 {language === 'es' ? 'Expediente completo' : 'Complete file'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenReports?.('R02')} disabled={!availableReports.R02?.available}>
                    <FileText className="w-4 h-4 mr-2" />
                    R02 {language === 'es' ? 'Resultados Guía I (ATS)' : 'Guide I Results (STE)'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenReports?.('R03')} disabled={!availableReports.R03?.available}>
                    <FileText className="w-4 h-4 mr-2" />
                    R03 {language === 'es' ? 'Resultados Guía II/III' : 'Guide II/III Results'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenReports?.('R04')} disabled={!availableReports.R04?.available}>
                    <FileText className="w-4 h-4 mr-2" />
                    R04 {language === 'es' ? 'Metodología de muestra' : 'Sampling methodology'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenReports?.('R05')} disabled={!availableReports.R05?.available}>
                    <FileText className="w-4 h-4 mr-2" />
                    R05 {language === 'es' ? 'Programa de Atención' : 'Attention Program'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenReports?.('R06')} disabled={!availableReports.R06?.available}>
                    <FileText className="w-4 h-4 mr-2" />
                    R06 {language === 'es' ? 'Comunicado a empleados' : 'Employee communique'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onOpenReports?.('R17')} disabled={!availableReports.R17?.available}>
                    <Download className="w-4 h-4 mr-2" />
                    R17 {language === 'es' ? 'Exportar datos (.csv)' : 'Export data (.csv)'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {/* Mini-widget 1: Tendencia IPA */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-2">{texts.ipaTrend}</p>
                {hasIpaHistory ? (
                  <>
                    <div className="flex items-end gap-1 h-12 mb-1">
                      {ipaHistory.map((item, idx) => (
                        <div 
                          key={idx}
                          className="flex-1 bg-teal-500 rounded-t"
                          style={{ height: `${(item.value / 100) * 100}%` }}
                          title={`${item.month}: ${item.value}%`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      {ipaHistory.map((item, idx) => (
                        <span key={idx}>{item.month}</span>
                      ))}
                    </div>
                    <Button variant="link" size="sm" className="w-full mt-2 text-xs p-0 h-auto" onClick={() => onOpenReports?.('R15')}>
                      {texts.viewHistory} →
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 italic">{language === 'es' ? 'Datos insuficientes' : 'Insufficient data'}</p>
                )}
              </div>

              {/* Mini-widget 2: Riesgo por Área */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-2">{texts.riskByArea}</p>
                {hasAreas && guideApplied ? (
                  <>
                    <div className="space-y-1">
                      {areaRisk.slice(0, 4).map((area, idx) => {
                        const riskColor = RISK_COLORS[area.level] || RISK_COLORS.NULO;
                        return (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <span className="truncate">{area.name}</span>
                            <span>{riskColor.emoji} <span className={riskColor.text}>{area.level}</span></span>
                          </div>
                        );
                      })}
                    </div>
                    <Button variant="link" size="sm" className="w-full mt-2 text-xs p-0 h-auto" onClick={() => onOpenReports?.('R07')}>
                      {texts.viewByArea} →
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 italic">{language === 'es' ? 'Sin datos de áreas' : 'No area data'}</p>
                )}
              </div>

              {/* Mini-widget 3: Buzón (consolidado) */}
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-2">{texts.mailbox30d}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{texts.received}:</span>
                    <span className="font-medium">{buzonStats.total_recibidos}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{texts.attended}:</span>
                    <span className="font-medium text-green-600">{buzonStats.atendidos}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'es' ? 'Pendientes' : 'Pending'}:</span>
                    <span className={`font-medium ${buzonStats.pendientes > 0 ? 'text-orange-600' : ''}`}>
                      {buzonStats.pendientes} {buzonStats.pendientes > 0 && '⚠'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 mt-2">
                  <Button variant="ghost" size="sm" className="flex-1 text-xs p-1 h-auto" onClick={onOpenBuzonEmpleado}>
                    <Send className="w-3 h-3 mr-1" />
                    {language === 'es' ? 'Enviar' : 'Send'}
                  </Button>
                  <Button variant="ghost" size="sm" className="flex-1 text-xs p-1 h-auto" onClick={onOpenBuzonAdmin}>
                    <Inbox className="w-3 h-3 mr-1" />
                    {language === 'es' ? 'Bandeja' : 'Inbox'}
                    {buzonStats.pendientes > 0 && (
                      <Badge className="bg-purple-600 text-white ml-1 text-xs px-1">{buzonStats.pendientes}</Badge>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Cards Row - Sin cambios */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-sm text-gray-500">{language === 'es' ? 'Centro de trabajo' : 'Workplace'}</p>
                <p className="font-semibold">{dashboard?.nombre_centro_trabajo || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-sm text-gray-500">{language === 'es' ? 'Trabajadores' : 'Workers'}</p>
                <p className="font-semibold">{dashboard?.numero_trabajadores || '-'}</p>
                <Badge variant="outline" className="mt-1">
                  {dashboard?.guia_aplicable === 'GUIDE_III' ? 'Guía III' : 'Guía II'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-sm text-gray-500">{language === 'es' ? 'Responsable' : 'Responsible'}</p>
                <p className="font-semibold">{dashboard?.responsable || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reevaluación Anticipada + Historial de Ciclos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Reevaluación Anticipada */}
        <Card className="border-orange-200" data-testid="reevaluacion-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" />
              {language === 'es' ? 'Reevaluación Anticipada' : 'Early Reevaluation'}
            </CardTitle>
            <CardDescription className="text-xs">
              {language === 'es'
                ? 'Numeral 7.9: Inicie un nuevo ciclo NOM-035 cuando haya cambios significativos en la organización.'
                : 'Section 7.9: Start a new NOM-035 cycle when significant organizational changes occur.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-3 p-2 bg-gray-50 rounded text-sm">
              <span className="text-gray-500">{language === 'es' ? 'Próxima reevaluación:' : 'Next reevaluation:'}</span>
              <span className="font-medium">
                {dashboard?.fecha_reevaluacion ? new Date(dashboard.fecha_reevaluacion).toLocaleDateString() : '-'}
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => setShowReeval(true)}
              data-testid="reevaluacion-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {language === 'es' ? 'Iniciar reevaluación anticipada' : 'Start early reevaluation'}
            </Button>
          </CardContent>
        </Card>

        {/* Historial de Ciclos */}
        <Card data-testid="cycle-history-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              {language === 'es' ? 'Historial de Ciclos' : 'Cycle History'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cycleHistory.length === 0 ? (
              <p className="text-sm text-gray-500">{language === 'es' ? 'No hay ciclos registrados' : 'No cycles registered'}</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {cycleHistory.map((cycle, idx) => (
                  <div key={cycle.id} className={`flex items-center justify-between p-2 rounded text-sm ${cycle.activo ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2">
                      {cycle.activo ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-gray-400" />}
                      <div>
                        <span className="font-medium">
                          {cycle.activo ? (language === 'es' ? 'Ciclo actual' : 'Current cycle') : `${language === 'es' ? 'Ciclo' : 'Cycle'} ${cycleHistory.length - idx}`}
                        </span>
                        {cycle.motivo_cierre && (
                          <p className="text-xs text-gray-500">
                            {REEVAL_MOTIVOS.find(m => m.value === cycle.motivo_cierre)?.[`label_${language}`] || cycle.motivo_cierre}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={cycle.activo ? 'default' : 'outline'} className={`text-xs ${cycle.activo ? 'bg-green-600' : ''}`}>
                        IPA {cycle.ipa_score}%
                      </Badge>
                      <p className="text-xs text-gray-400 mt-1">
                        {cycle.fecha_inicio ? new Date(cycle.fecha_inicio).toLocaleDateString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* M-05: Magic Link Admin Card */}
      <MagicLinkAdmin empresaId={empresaId} />

      {/* Reevaluación Dialog */}
      <Dialog open={showReeval} onOpenChange={setShowReeval}>
        <DialogContent data-testid="reevaluacion-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Iniciar Reevaluación Anticipada' : 'Start Early Reevaluation'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'Se cerrará el ciclo actual y se creará uno nuevo vinculado. Los datos del ciclo anterior se conservarán.'
                : 'The current cycle will be closed and a new linked one will be created. Previous cycle data will be preserved.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{language === 'es' ? 'Motivo *' : 'Reason *'}</Label>
              <select 
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                value={reevalMotivo}
                onChange={(e) => setReevalMotivo(e.target.value)}
                data-testid="reevaluacion-motivo-select"
              >
                <option value="">{language === 'es' ? 'Seleccionar motivo...' : 'Select reason...'}</option>
                {REEVAL_MOTIVOS.map(m => (
                  <option key={m.value} value={m.value}>
                    {language === 'es' ? m.label_es : m.label_en}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>{language === 'es' ? 'Detalle (opcional)' : 'Detail (optional)'}</Label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px] mt-1"
                value={reevalDetalle}
                onChange={(e) => setReevalDetalle(e.target.value)}
                placeholder={language === 'es' ? 'Describe el motivo de la reevaluación...' : 'Describe the reason for reevaluation...'}
                data-testid="reevaluacion-detalle-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReeval(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleReevaluacion} 
              disabled={reevalLoading || !reevalMotivo}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="reevaluacion-confirm-btn"
            >
              {reevalLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              {language === 'es' ? 'Confirmar' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NOM035DashboardV2;
